import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { Alert, Card, Empty, Input, Select, Skeleton, Space, Table, Tag, Typography } from 'antd';
import type { AuditAction, AuditEntryRow } from '@velora/contracts';
import { productApi } from '../../../api';

/**
 * Admin surface — Audit log. Reads audit_log rows in reverse-time.
 * Every mutation route writes here via appendAudit(). Append-only per A-02;
 * no delete endpoint exists.
 */
export function AuditPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const [entityType, setEntityType] = useState<string | undefined>();
  const [action, setAction] = useState<AuditAction | undefined>();

  const q = useQuery({
    queryKey: ['audit', institution, entityType, action],
    queryFn: () => productApi.listAudit(institution, { entity_type: entityType, action, limit: 500 }),
    enabled: !!institution,
  });

  const rows = q.data?.rows ?? [];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div>
        <Typography.Title level={3} style={{ margin: 0 }}>Audit log</Typography.Title>
        <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
          Every mutation. Append-only per A-02 — no delete endpoint exists.
          Kept forever; exports (CSV, board-format) ship with the Compliance module.
        </Typography.Text>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Input
          size="small"
          placeholder="Entity type (e.g. student, receipt)"
          value={entityType ?? ''}
          onChange={(e) => setEntityType(e.target.value || undefined)}
          allowClear
          style={{ maxWidth: 240 }}
        />
        <Select
          size="small"
          value={action ?? 'all'}
          onChange={(v) => setAction(v === 'all' ? undefined : (v as AuditAction))}
          style={{ width: 140 }}
          options={[
            { value: 'all', label: 'Any action' },
            { value: 'create', label: 'create' },
            { value: 'update', label: 'update' },
            { value: 'delete', label: 'delete' },
            { value: 'mark', label: 'mark' },
            { value: 'submit', label: 'submit' },
          ]}
        />
      </div>

      <Card size="small" title={`${rows.length} entries`}>
        {q.isLoading && <Skeleton active paragraph={{ rows: 6 }} />}
        {q.isError && <Alert type="error" showIcon message="Could not load audit log" />}
        {q.data && rows.length === 0 && (
          <Empty description="No audit entries yet — write something (add a student, create a receipt) and it lands here." style={{ padding: 20 }} />
        )}
        {rows.length > 0 && (
          <Table<AuditEntryRow>
            size="small"
            pagination={{ pageSize: 20 }}
            rowKey="id"
            dataSource={rows}
            columns={[
              {
                title: 'When',
                dataIndex: 'at',
                key: 'at',
                width: 170,
                render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 12 }}>{fmtTime(v)}</span>,
              },
              {
                title: 'Actor',
                dataIndex: 'actor_email',
                key: 'actor',
                width: 200,
                render: (v: string | null) => v ?? <Typography.Text type="secondary">system</Typography.Text>,
              },
              {
                title: 'Entity',
                dataIndex: 'entity_type',
                key: 'et',
                width: 140,
                render: (v: string) => <Tag>{v}</Tag>,
              },
              {
                title: 'Action',
                dataIndex: 'action',
                key: 'a',
                width: 90,
                render: (v: AuditAction) => <Tag color={ACTION_TONE[v]}>{v}</Tag>,
              },
              { title: 'Summary', dataIndex: 'summary', key: 's' },
              {
                title: 'Changes',
                dataIndex: 'changes',
                key: 'c',
                width: 260,
                render: (v: Record<string, unknown> | null) =>
                  v ? (
                    <code style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 11, color: '#5b6478' }}>
                      {JSON.stringify(v)}
                    </code>
                  ) : (
                    <Typography.Text type="secondary">—</Typography.Text>
                  ),
              },
            ]}
          />
        )}
      </Card>
    </Space>
  );
}

const ACTION_TONE: Record<AuditAction, string> = {
  create: 'success',
  update: 'processing',
  delete: 'error',
  mark: 'purple',
  submit: 'blue',
};

function fmtTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}
